import React, { useEffect, useState, useRef } from "react";
import { 
    Modal, ModalBody, Table
} from "reactstrap";
import { ContentLoader } from "../../../../components/Common";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { useTenant } from "../../../../hooks/useTenant";
import EvaluationService from "../../../../services/evaluation_service";
import PrintableEventReport from "./PrintableEventReport";

/**
 * EventReportModal
 * Relatório de resultados de avaliação no estilo "Lista de Competição".
 * Usa o mesmo padrão do SalesReceiptModal: modal + componente oculto + react-to-print.
 */
const EventReportModal = ({ isOpen, toggle, event }) => {
    const { idTenant, tenantSettings, tenantName, tenantLogo } = useTenant();
    const printRef = useRef();
    
    const [loading, setLoading] = useState(true);
    const [evaluations, setEvaluations] = useState([]);

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        content: () => printRef.current,
        documentTitle: `Relatorio_${event?.name || "Evento"}`
    });

    useEffect(() => {
        if (isOpen && event?.id && idTenant) {
            loadEvaluations();
        } else {
            setEvaluations([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, event?.id, idTenant]);

    const loadEvaluations = async () => {
        try {
            setLoading(true);
            const data = await EvaluationService.getAllByEvent(idTenant, event.id);
            const sorted = data.sort((a, b) => {
                const nameA = (a.memberName || "").toUpperCase();
                const nameB = (b.memberName || "").toUpperCase();
                return nameA.localeCompare(nameB);
            });
            setEvaluations(sorted);
        } catch (error) {
            console.error("Erro ao carregar avaliações do evento", error);
        } finally {
            setLoading(false);
        }
    };

    // Verifica fundamentais (lê do doc desnormalizado)
    const renderFundamentalStatus = (evaluation) => {
        const objectivesList = evaluation.objectives || [];
        const topicsList = objectivesList.flatMap(obj => obj.topics || []);
        if (topicsList.length === 0) return <span className="text-muted">-</span>;

        const fundamentalTopics = topicsList.filter(c => c.isFundamental);
        if (fundamentalTopics.length === 0) return <span className="text-muted">N/A</span>;

        const passedAmount = fundamentalTopics.filter(c => c.achieved).length;
        const totalAmount = fundamentalTopics.length;
        return (passedAmount === totalAmount) ? (
            <span className="text-success fw-bold"><i className="mdi mdi-check"></i> Sim</span>
        ) : (
            <span className="text-danger fw-bold"><i className="mdi mdi-close"></i> Não</span>
        );
    };

    // Status final: "AP" para aprovado, "-" para quem não passou
    const renderStatus = (ev) => {
        const passed = ev.status === "PASSED" || ev.xpPassAwarded;
        if (passed) return <span className="text-success fw-bold">AP</span>;
        return <span className="text-muted">-</span>;
    };

    const approvedCount = evaluations.filter(ev => ev.status === "PASSED" || ev.xpPassAwarded).length;

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable>
            <div className="modal-header border-bottom d-flex align-items-center justify-content-between w-100">
                <div className="d-flex align-items-center gap-2">
                    <i className="mdi mdi-format-list-numbered fs-4 text-primary"></i>
                    <h5 className="modal-title mb-0">Lista de Resultados - {event?.name}</h5>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <button
                        className="btn btn-primary d-flex align-items-center justify-content-center rounded-2 shadow-sm p-0 border-0"
                        style={{ width: "38px", height: "38px" }}
                        onClick={handlePrint}
                        disabled={loading || evaluations?.length === 0}
                        title="Imprimir Relatório"
                    >
                        <i className="mdi mdi-printer font-size-18"></i>
                    </button>
                    <button type="button" className="btn-close m-0" onClick={toggle}></button>
                </div>
            </div>
            <ModalBody className="p-4">

                {/* Info do evento (sem logo — logo fica somente na página de impressão) */}
                <div className="border-bottom pb-3 mb-4">
                    <div className="text-muted small">
                        <strong>Período:</strong> {event?.startDate ? moment(event.startDate).format("DD/MM/YYYY") : '--'} a {event?.endDate ? moment(event.endDate).format("DD/MM/YYYY") : '--'}
                        <span className="mx-2">|</span>
                        <strong>Categoria:</strong> {event?.type === 'test' ? 'Performance Física' : 'Técnica e Avaliação'}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <ContentLoader message="Carregando relatório..." />
                    </div>
                ) : (
                    <>
                        {evaluations.length === 0 ? (
                            <div className="alert alert-warning text-center py-4">
                                <i className="mdi mdi-alert-circle-outline fs-3 mb-2 d-block"></i>
                                Não há avaliações registradas para este evento ainda.
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <Table striped bordered hover size="sm" className="align-middle mb-0" style={{ backgroundColor: '#fff' }}>
                                        <thead className="table-dark">
                                            <tr>
                                                <th style={{ width: "4%" }} className="text-center">#</th>
                                                <th style={{ width: "28%" }}>Atleta / Aluno</th>
                                                <th>Professor</th>
                                                <th>Nível</th>
                                                <th className="text-center">Aprov.</th>
                                                {event?.type === 'technical' && <th className="text-center">Fund.</th>}
                                                <th className="text-center" style={{ width: "7%" }}>Status</th>
                                                <th>Avaliador</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {evaluations.map((ev, index) => (
                                                <tr key={ev.id || index}>
                                                    <td className="text-center fw-bold text-muted">{index + 1}</td>
                                                    <td className="fw-bold text-dark text-uppercase">{ev.memberName || ev.memberName || "Desconhecido"}</td>
                                                    <td className="text-muted small text-uppercase">{ev.teacherName || ev.employeeName || "-"}</td>

                                                    <td className="text-muted fw-medium">{ev.activityName || ev.levelName || "-"}</td>

                                                    <td className="text-center fw-bold text-primary">
                                                        {ev.progressPercent != null ? `${ev.progressPercent}%` : "-"}
                                                    </td>

                                                    {event?.type === 'technical' && (
                                                        <td className="text-center">{renderFundamentalStatus(ev)}</td>
                                                    )}

                                                    <td className="text-center fw-bold text-uppercase">{renderStatus(ev)}</td>

                                                    <td className="text-muted small text-uppercase">{ev.employeeName || ev.updatedBy?.name || "Sistema"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Resumo */}
                                <div className="d-flex justify-content-between align-items-center mt-3 px-2 text-muted small">
                                    <span><strong>Total:</strong> {evaluations.length} avaliações</span>
                                    <span><strong>Aprovados:</strong> {approvedCount} de {evaluations.length} ({evaluations.length > 0 ? Math.round((approvedCount / evaluations.length) * 100) : 0}%)</span>
                                </div>
                            </>
                        )}
                    </>
                )}
            </ModalBody>



            {/* Relatório oculto para impressão via react-to-print */}
            <div style={{ display: "none" }}>
                <div ref={printRef}>
                    <PrintableEventReport
                        evaluations={evaluations}
                        event={event}
                        tenantName={tenantName || tenantSettings?.name}
                        tenantLogo={tenantLogo || tenantSettings?.logo}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default EventReportModal;
